<template>
  <section>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-0">{{ t("ecolabels.submissions.title") }}</h5>
        <p class="text-muted mb-0 small">
          {{
            isAdmin
              ? t("ecolabels.submissions.subtitle_admin")
              : t("ecolabels.submissions.subtitle_seller")
          }}
        </p>
      </div>
      <div class="d-flex align-items-center gap-2">
        <!-- Reviewed batches leave the default view so the queue stays a queue
             (customer feedback: "after approving, the request stays in pending"). -->
        <div class="btn-group btn-group-sm" role="group">
          <button
            :class="[
              'btn',
              batchFilter === 'pending'
                ? 'btn-secondary'
                : 'btn-outline-secondary',
            ]"
            @click="batchFilter = 'pending'"
          >
            {{ t("ecolabels.submissions.filter_pending") }}
          </button>
          <button
            :class="[
              'btn',
              batchFilter === 'all' ? 'btn-secondary' : 'btn-outline-secondary',
            ]"
            @click="batchFilter = 'all'"
          >
            {{ t("ecolabels.submissions.filter_all") }}
          </button>
        </div>
        <!-- One click folds every open layer (batches, detail panels, duplicate
             comparisons) — long pending lists stack up fast (customer feedback). -->
        <button
          v-if="anythingExpanded"
          class="btn btn-outline-secondary btn-sm text-nowrap"
          @click="collapseAll"
        >
          <font-awesome-icon icon="chevron-up" class="me-1" />
          {{ t("ecolabels.submissions.collapse_all") }}
        </button>
        <MultiSelect
          v-model="selectedColumns"
          :options="columnOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="t('ecolabels.select_columns')"
          class="column-selector"
          display="chip"
          :focusOnHover="false"
        >
          <template #value="{ value }">
            <span class="text-body-tertiary">
              <font-awesome-icon icon="table-columns" class="me-1" />
              {{
                t("ecolabels.columns_selected", { count: value?.length ?? 0 })
              }}
            </span>
          </template>
        </MultiSelect>
        <router-link class="btn btn-primary btn-sm" to="/ecolabels/upload">
          <font-awesome-icon icon="plus" class="me-1" />
          {{ t("ecolabels.submissions.submit_devices") }}
        </router-link>
      </div>
    </div>

    <LoadingIndicatorComponent v-if="loading" />

    <div
      v-else-if="!batches.length"
      class="text-center text-muted py-5 border rounded"
    >
      {{ t("ecolabels.submissions.empty") }}
    </div>

    <div
      v-else-if="!visibleBatches.length"
      class="text-center text-muted py-5 border rounded"
    >
      <div class="mb-2">{{ t("ecolabels.submissions.no_pending") }}</div>
      <button class="btn btn-link btn-sm" @click="batchFilter = 'all'">
        {{ t("ecolabels.submissions.show_all") }}
      </button>
    </div>

    <DataTable
      v-else
      v-model:expandedRows="expandedBatches"
      :value="visibleBatches"
      dataKey="id"
      @row-expand="onBatchExpand"
    >
      <Column expander headerStyle="width: 3rem" />
      <Column
        field="created_at"
        :header="t('ecolabels.submissions.submitted_on')"
        headerStyle="width: 10rem"
      >
        <template #body="{ data }">{{ formatDate(data.created_at) }}</template>
      </Column>
      <Column
        v-if="isAdmin"
        field="company_name"
        :header="t('ecolabels.submissions.seller')"
      />
      <Column
        field="device_count"
        :header="t('ecolabels.submissions.devices')"
        headerStyle="width: 7rem"
      />
      <Column
        :header="t('ecolabels.submissions.status')"
        headerStyle="width: 22rem"
      >
        <template #body="{ data }">
          <span
            v-if="data.pending_count"
            class="badge bg-warning text-dark me-1"
          >
            {{
              t("ecolabels.submissions.pending_badge", {
                count: data.pending_count,
              })
            }}
          </span>
          <span v-if="data.approved_count" class="badge bg-success me-1">
            {{
              t("ecolabels.submissions.approved_badge", {
                count: data.approved_count,
              })
            }}
          </span>
          <span v-if="data.rejected_count" class="badge bg-danger me-1">
            {{
              t("ecolabels.submissions.rejected_badge", {
                count: data.rejected_count,
              })
            }}
          </span>
          <span v-if="data.merged_count" class="badge bg-info text-dark me-1">
            {{
              t("ecolabels.submissions.merged_badge", {
                count: data.merged_count,
              })
            }}
          </span>
          <span v-if="data.flagged_duplicate_count" class="badge bg-secondary">
            {{
              t("ecolabels.submissions.flagged_badge", {
                count: data.flagged_duplicate_count,
              })
            }}
          </span>
        </template>
      </Column>

      <template #expansion="{ data }">
        <div class="p-2">
          <LoadingIndicatorComponent v-if="!batchDevices[data.id]" />
          <!-- The device table scrolls horizontally in here: with many columns visible it
               is wider than the page, and the outer table cell would otherwise clip it
               with no way to scroll (customer feedback). -->
          <div v-else class="table-responsive device-table-scroller">
            <table class="table table-sm align-middle mb-0">
              <thead>
                <tr class="small text-muted">
                  <th style="width: 2.5rem"></th>
                  <th v-for="col in visibleColumns" :key="col">
                    {{ t(ALL_ECOLABEL_COLUMNS[col].header) }}
                  </th>
                  <th>{{ t("ecolabels.submissions.duplicates_column") }}</th>
                  <th>{{ t("ecolabels.table.status") }}</th>
                  <th v-if="isAdmin" class="text-end">
                    {{ t("ecolabels.submissions.actions") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="device in batchDevices[data.id]"
                  :key="device.id"
                >
                  <tr>
                    <td>
                      <!-- Leading expander (same pattern as the batch rows above):
                         toggles the read-only view of the submitted device fields
                         (review status/duplicates stay in their own columns) -->
                      <button
                        class="btn btn-link btn-sm p-0 text-body-secondary"
                        :title="t('ecolabels.submissions.details')"
                        :aria-expanded="detailsFor === device.id"
                        @click="toggleDetails(device)"
                      >
                        <font-awesome-icon
                          :icon="
                            detailsFor === device.id
                              ? 'chevron-down'
                              : 'chevron-right'
                          "
                        />
                      </button>
                    </td>
                    <td v-for="col in visibleColumns" :key="col">
                      <template v-if="col === 'plant_ecolabel_id'">
                        {{ device.plant_ecolabel_id || "-" }}
                        <span
                          v-if="
                            device.registration_id_quality &&
                            ['SUSPECT', 'MULTIPLE', 'MISSING'].includes(
                              device.registration_id_quality
                            )
                          "
                          class="badge bg-secondary ms-1"
                          :title="t('ecolabels.upload.flag_reg_id_hint')"
                        >
                          {{ device.registration_id_quality }}
                        </span>
                      </template>
                      <template v-else>{{
                        deviceCellValue(device, col)
                      }}</template>
                    </td>
                    <td>
                      <!-- Pair details are an admin capability; sellers see the count only. -->
                      <button
                        v-if="duplicateCounts[device.id] && isAdmin"
                        class="badge bg-warning text-dark border-0"
                        :title="t('ecolabels.submissions.duplicates_hint')"
                        @click="toggleDuplicates(device)"
                      >
                        <font-awesome-icon
                          icon="triangle-exclamation"
                          class="me-1"
                        />
                        {{
                          t("ecolabels.submissions.duplicates_badge", {
                            count: duplicateCounts[device.id],
                          })
                        }}
                      </button>
                      <span
                        v-else-if="duplicateCounts[device.id]"
                        class="badge bg-warning text-dark"
                      >
                        <font-awesome-icon
                          icon="triangle-exclamation"
                          class="me-1"
                        />
                        {{
                          t("ecolabels.submissions.duplicates_badge", {
                            count: duplicateCounts[device.id],
                          })
                        }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="statusBadgeClass(device.status)"
                      >
                        {{
                          t(`ecolabels.statuses.${device.status.toLowerCase()}`)
                        }}
                      </span>
                      <div
                        v-if="device.rejection_reason"
                        class="small text-danger"
                      >
                        {{ device.rejection_reason }}
                      </div>
                    </td>
                    <td v-if="isAdmin" class="text-end text-nowrap">
                      <template
                        v-if="device.status === EcolabelStatus.PENDING_REVIEW"
                      >
                        <button
                          class="btn btn-success btn-sm me-1"
                          :disabled="actingOn !== null"
                          @click="approve(data.id, device)"
                        >
                          {{ t("ecolabels.submissions.approve") }}
                        </button>
                        <button
                          class="btn btn-outline-danger btn-sm"
                          :disabled="actingOn !== null"
                          @click="startReject(device)"
                        >
                          {{ t("ecolabels.submissions.reject") }}
                        </button>
                      </template>
                    </td>
                  </tr>
                  <tr v-if="rejectingDevice?.id === device.id">
                    <td :colspan="expandedRowColspan" class="bg-light">
                      <div class="d-flex gap-2 align-items-center">
                        <input
                          v-model="rejectionReason"
                          class="form-control form-control-sm"
                          :placeholder="
                            t(
                              'ecolabels.submissions.rejection_reason_placeholder'
                            )
                          "
                        />
                        <button
                          class="btn btn-danger btn-sm"
                          :disabled="
                            !rejectionReason.trim() || actingOn !== null
                          "
                          @click="confirmReject(data.id, device)"
                        >
                          {{ t("ecolabels.submissions.reject") }}
                        </button>
                        <button
                          class="btn btn-outline-secondary btn-sm"
                          @click="rejectingDevice = null"
                        >
                          {{ t("common.cancel") }}
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="duplicatesFor === device.id">
                    <td :colspan="expandedRowColspan" class="bg-light">
                      <div
                        v-if="overrideRequiredFor === device.id"
                        class="alert alert-warning d-flex justify-content-between align-items-center py-2 mb-2"
                      >
                        <span class="small">
                          {{ t("ecolabels.submissions.override_confirm") }}
                        </span>
                        <button
                          class="btn btn-warning btn-sm text-nowrap ms-2"
                          :disabled="actingOn !== null"
                          @click="approveWithOverride(data.id, device)"
                        >
                          {{ t("ecolabels.submissions.approve_anyway") }}
                        </button>
                      </div>
                      <LoadingIndicatorComponent
                        v-if="!devicePairs[device.id]"
                      />
                      <div
                        v-else-if="!devicePairs[device.id].length"
                        class="text-muted small"
                      >
                        {{ t("ecolabels.submissions.no_duplicates") }}
                      </div>
                      <DuplicatePairCard
                        v-for="pair in devicePairs[device.id]"
                        v-else
                        :key="pair.id"
                        :pair="pair"
                        compact
                        :subjectDeviceId="device.id"
                        @resolved="onPairResolved(data.id, device)"
                      />
                    </td>
                  </tr>
                  <tr v-if="detailsFor === device.id">
                    <td
                      :colspan="expandedRowColspan"
                      class="device-details-cell"
                    >
                      <!-- Sticks to the visible width while the table scrolls, so the
                         panel's right-hand column is never cut off. -->
                      <div class="device-details p-3">
                        <!-- Admins edit a draft copy in place and save before deciding —
                         sellers often submit wrong details (customer feedback). -->
                        <template v-if="isAdmin && editableDraft">
                          <DeviceFieldsPanel
                            :device="editableDraft"
                            :countryOptions="countryOptions"
                            :irecShortcut="false"
                          />
                          <div class="d-flex justify-content-end gap-2 mt-3">
                            <button
                              class="btn btn-outline-secondary btn-sm"
                              :disabled="savingEdit"
                              @click="resetDeviceEdits(device)"
                            >
                              {{ t("common.cancel") }}
                            </button>
                            <ActionButtonWithSpinner
                              variant="btn-primary"
                              :busy="savingEdit"
                              @click="saveDeviceEdits(device, data.id)"
                            >
                              {{ t("ecolabels.submissions.save_changes") }}
                            </ActionButtonWithSpinner>
                          </div>
                        </template>
                        <DeviceFieldsPanel v-else :device="device" readonly />

                        <!-- Internal review notes: the EKOenergy team's scratchpad for
                         devices that need several passes (replaces their spreadsheet).
                         Admin-only in both directions. -->
                        <div v-if="isAdmin" class="internal-notes mt-3 pt-3">
                          <div class="fw-semibold small mb-1">
                            <font-awesome-icon
                              icon="lock"
                              class="me-2 text-body-secondary"
                            />
                            {{ t("ecolabels.submissions.internal_notes") }}
                          </div>
                          <div class="text-muted small mb-2">
                            {{ t("ecolabels.submissions.internal_notes_hint") }}
                          </div>
                          <LoadingIndicatorComponent
                            v-if="deviceNotes === null"
                          />
                          <div
                            v-for="note in deviceNotes"
                            v-else
                            :key="note.id"
                            class="small mb-2"
                          >
                            <span class="fw-semibold">{{
                              note.author_name
                            }}</span>
                            <span class="text-muted ms-2">{{
                              formatDate(note.created_at)
                            }}</span>
                            <div class="note-text">{{ note.note }}</div>
                          </div>
                          <div class="d-flex gap-2 mt-2">
                            <input
                              v-model="newNote"
                              class="form-control form-control-sm"
                              :placeholder="
                                t('ecolabels.submissions.note_placeholder')
                              "
                              @keyup.enter="submitNote"
                            />
                            <button
                              class="btn btn-outline-primary btn-sm text-nowrap"
                              :disabled="!newNote.trim() || addingNote"
                              @click="submitNote"
                            >
                              {{ t("ecolabels.submissions.add_note") }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </DataTable>
  </section>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useLocalStorage } from "@vueuse/core";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import MultiSelect from "primevue/multiselect";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  formatDate as formatDateOnly,
  formatDateTimeIntoDate,
} from "@/composables/formatUtils";
import { ecolabelsApi } from "../api/ecolabelsApi";
import { isoCountryOptions, withUnknownCodes } from "../countryOptions";
import {
  invalidNumberFields,
  toFiniteNumber,
} from "../upload/deviceUploadMapping";
import {
  ALL_ECOLABEL_COLUMNS,
  type EcolabelColumnKey,
} from "../ecolabelTableColumns";
import type {
  DeviceNote,
  DuplicatePair,
  EcolabelDevice,
  SubmissionBatchSummary,
} from "../api/ecolabelsTypes";
import { EcolabelStatus } from "../api/ecolabelsTypes";
import DuplicatePairCard from "../duplicates/DuplicatePairCard.vue";
import DeviceFieldsPanel, {
  type DeviceFieldValues,
} from "../upload/DeviceFieldsPanel.vue";

const props = defineProps<{
  isAdmin: boolean;
}>();

const emit = defineEmits<{
  "devices-changed": [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler, globalWarningHandler } =
  useGlobalHandlers();

// Column picker for the expanded batch device table. Fixed order; the picker toggles
// which of these render. Duplicates/status/actions stay always visible.
const SUBMISSION_COLUMN_KEYS: EcolabelColumnKey[] = [
  "name",
  "country",
  "address",
  "latitude",
  "longitude",
  "renewable_type",
  "technology_code",
  "registration_authority",
  "plant_ecolabel_id",
  "capacity",
  "cod",
  "public_notes",
];

const DEFAULT_SUBMISSION_COLUMNS: EcolabelColumnKey[] = [
  "name",
  "country",
  "renewable_type",
  "registration_authority",
  "plant_ecolabel_id",
];

const selectedColumns = useLocalStorage<EcolabelColumnKey[]>(
  "ecolabel-submissions-columns",
  DEFAULT_SUBMISSION_COLUMNS
);

const columnOptions = computed(() =>
  SUBMISSION_COLUMN_KEYS.map((key) => ({
    label: t(ALL_ECOLABEL_COLUMNS[key].header),
    value: key,
  }))
);

const visibleColumns = computed(() =>
  SUBMISSION_COLUMN_KEYS.filter((key) => selectedColumns.value.includes(key))
);

// Picked columns + details + duplicates + status (+ actions for admins).
const expandedRowColspan = computed(
  () => visibleColumns.value.length + 3 + (props.isAdmin ? 1 : 0)
);

// Reviewed batches leave the default admin view; sellers land on the full history.
// isAdmin arrives async (account flags load after mount), so the default is applied
// when it flips rather than read once at setup.
const batchFilter = ref<"pending" | "all">("all");
watch(
  () => props.isAdmin,
  (isAdmin) => {
    if (isAdmin) batchFilter.value = "pending";
  },
  { immediate: true }
);
const visibleBatches = computed(() =>
  batchFilter.value === "all"
    ? batches.value
    : batches.value.filter((batch) => batch.pending_count > 0)
);

// Country picker options for the inline editor (full ISO list; a device imported with a
// non-ISO code still shows it instead of a blank).
const countryOptions = computed(() =>
  withUnknownCodes(isoCountryOptions(), [editableDraft.value?.country])
);

// All-fields view of one device. Admins get an editable draft copy (sellers often
// submit wrong details); sellers see it read-only.
const detailsFor = ref<number | null>(null);
const editableDraft = ref<DeviceFieldValues | null>(null);
const savingEdit = ref(false);
const deviceNotes = ref<DeviceNote[] | null>(null);
const newNote = ref("");
const addingNote = ref(false);
// Newest-wins for the note thread: a GET snapshot read before a POST committed must
// not overwrite the appended note when it resolves later.
let notesGeneration = 0;
// Bumped every time a panel opens: a POST continuation from a previous visit to the
// SAME device must not mutate the reopened panel's input or thread.
let panelInstance = 0;

const buildDraft = (device: EcolabelDevice): DeviceFieldValues => ({
  name: device.name ?? null,
  country: device.country ?? null,
  address: device.address ?? null,
  latitude: device.latitude ?? null,
  longitude: device.longitude ?? null,
  renewable_type: device.renewable_type ?? null,
  technology_code: device.technology_code ?? null,
  plant_ecolabel_id: device.plant_ecolabel_id ?? null,
  registration_authority: device.registration_authority ?? null,
  capacity: device.capacity ?? null,
  capacity_units: device.capacity_units,
  cod: device.cod ?? null,
  relevant_link: device.relevant_link ?? null,
  public_notes: device.public_notes ?? null,
});

const openDetails = (device: EcolabelDevice) => {
  panelInstance += 1;
  detailsFor.value = device.id;
  editableDraft.value = props.isAdmin ? buildDraft(device) : null;
  newNote.value = "";
  if (props.isAdmin) void loadNotes(device.id);
};

const closeDetails = () => {
  detailsFor.value = null;
  editableDraft.value = null;
  deviceNotes.value = null;
};

const toggleDetails = (device: EcolabelDevice) => {
  if (detailsFor.value === device.id) closeDetails();
  else openDetails(device);
};

/** Guided review: auto-open the first device still awaiting review, so a batch can be
 * walked top to bottom — each approve/reject advances to the next pending one. */
const expandFirstPending = (batchId: number) => {
  const next = (batchDevices.value[batchId] ?? []).find(
    (device) => device.status === EcolabelStatus.PENDING_REVIEW
  );
  if (next) openDetails(next);
  else closeDetails();
};

const resetDeviceEdits = (device: EcolabelDevice) => {
  editableDraft.value = buildDraft(device);
};

const saveDeviceEdits = async (device: EcolabelDevice, batchId: number) => {
  const draft = editableDraft.value;
  if (!draft) return;
  if (invalidNumberFields(draft).length > 0) {
    globalWarningHandler(t("ecolabels.submissions.invalid_number_fields"), "");
    return;
  }
  savingEdit.value = true;
  try {
    await ecolabelsApi.updateDevice(device.id, {
      // Mandatory columns can't be cleared, only changed — an emptied input falls
      // back to the persisted value (and the post-save refresh shows it restored).
      name: draft.name?.trim() || device.name,
      country: draft.country?.trim() || device.country,
      address: draft.address,
      latitude: toFiniteNumber(draft.latitude),
      longitude: toFiniteNumber(draft.longitude),
      renewable_type: draft.renewable_type,
      technology_code: draft.technology_code,
      plant_ecolabel_id: draft.plant_ecolabel_id,
      registration_authority: draft.registration_authority,
      capacity: toFiniteNumber(draft.capacity),
      cod: draft.cod || null,
      relevant_link: draft.relevant_link,
      public_notes: draft.public_notes,
    });
    await reloadBatchDevices(batchId);
    // The admin may have opened another device while the PUT was in flight — only
    // refresh the draft if this panel is still the one being edited.
    if (detailsFor.value === device.id) {
      const refreshed = (batchDevices.value[batchId] ?? []).find(
        (d) => d.id === device.id
      );
      if (refreshed) editableDraft.value = buildDraft(refreshed);
    }
    globalSuccessHandler(t("ecolabels.submissions.device_saved"), "");
    emit("devices-changed");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    savingEdit.value = false;
  }
};

const loadNotes = async (deviceId: number) => {
  const generation = ++notesGeneration;
  deviceNotes.value = null;
  try {
    const response = await ecolabelsApi.listDeviceNotes(deviceId);
    if (detailsFor.value === deviceId && generation === notesGeneration) {
      deviceNotes.value = response.data.notes;
    }
  } catch (error) {
    // Only the open panel's CURRENT load settles state and reports; a superseded
    // request's failure must not clear an already-updated thread.
    if (detailsFor.value === deviceId && generation === notesGeneration) {
      deviceNotes.value = [];
      globalErrorHandler(error);
    }
  }
};

const submitNote = async () => {
  const deviceId = detailsFor.value;
  const instance = panelInstance;
  const text = newNote.value.trim();
  if (!deviceId || !text || addingNote.value) return;
  addingNote.value = true;
  try {
    const response = await ecolabelsApi.addDeviceNote(deviceId, text);
    // The note belongs to the panel visit it was written in — a continuation landing
    // after a switch (even back to the same device) must not touch the new visit's
    // thread or wipe freshly typed text.
    if (detailsFor.value === deviceId && instance === panelInstance) {
      if (deviceNotes.value === null) {
        // The initial load hasn't landed — refetch instead of appending, or the
        // thread would show only the new note until the panel reopens.
        void loadNotes(deviceId);
      } else if (!deviceNotes.value.some((n) => n.id === response.data.id)) {
        // Skip the append when a concurrent GET already delivered this note.
        notesGeneration += 1; // an in-flight GET snapshot is now stale
        deviceNotes.value = [...deviceNotes.value, response.data];
      }
      newNote.value = "";
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    addingNote.value = false;
  }
};

const deviceCellValue = (
  device: EcolabelDevice,
  col: EcolabelColumnKey
): string => {
  switch (col) {
    case "capacity":
      return device.capacity != null
        ? `${device.capacity} ${device.capacity_units}`
        : "-";
    case "cod":
      return device.cod ? formatDateOnly(device.cod) : "-";
    default: {
      const value = (device as unknown as Record<string, unknown>)[col];
      return value == null || value === "" ? "-" : String(value);
    }
  }
};

const loading = ref(true);
const batches = ref<SubmissionBatchSummary[]>([]);
const expandedBatches = ref<Record<number, boolean>>({});
const batchDevices = ref<Record<number, EcolabelDevice[]>>({});
const duplicateCounts = ref<Record<number, number>>({});
const devicePairs = ref<Record<number, DuplicatePair[]>>({});
const duplicatesFor = ref<number | null>(null);
const rejectingDevice = ref<EcolabelDevice | null>(null);
const rejectionReason = ref("");
// While set, EVERY action control is disabled (not just this device's): the
// post-action refreshes must land before another action can start, or out-of-order
// responses could paint stale statuses over fresh ones.
const actingOn = ref<number | null>(null);
// Set when approval got 409'd by unresolved strong duplicates; renders the inline
// "approve anyway" option next to the surfaced pairs (no blocking window.confirm).
const overrideRequiredFor = ref<number | null>(null);

const anythingExpanded = computed(
  () =>
    Object.values(expandedBatches.value).some(Boolean) ||
    detailsFor.value !== null ||
    duplicatesFor.value !== null ||
    rejectingDevice.value !== null
);

// Bumped by collapseAll: an in-flight action's post-await expansion (auto-opening the
// next pending device, or surfacing duplicate pairs on a 409) must not undo an explicit
// collapse that happened while its request was running.
let expansionGeneration = 0;

/** Fold every open layer at once: expanded batches, device detail panels, duplicate
 * comparisons, and a half-typed rejection input. */
const collapseAll = () => {
  expansionGeneration += 1;
  expandedBatches.value = {};
  closeDetails();
  duplicatesFor.value = null;
  rejectingDevice.value = null;
  overrideRequiredFor.value = null;
};

onMounted(load);

// Background refreshes (after approve/reject) must not flip `loading` — the
// spinner v-if would remount the whole table, losing scroll and expanded batches.
async function load(options: { background?: boolean } = {}) {
  if (!options.background) loading.value = true;
  try {
    const response = await ecolabelsApi.listSubmissionBatches();
    batches.value = response.data.batches;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loading.value = false;
  }
}

async function onBatchExpand(event: { data: SubmissionBatchSummary }) {
  const generation = expansionGeneration;
  const applied = await reloadBatchDevices(event.data.id);
  if (applied && generation === expansionGeneration) {
    expandFirstPending(event.data.id);
  }
}

// Newest-wins per batch: an expand's device fetch can still be in flight when an
// approval's refresh lands — the older response must not paint stale statuses back.
const batchReloadGenerations = new Map<number, number>();

/** Returns whether this call's response was applied (false when superseded). */
async function reloadBatchDevices(batchId: number): Promise<boolean> {
  const generation = (batchReloadGenerations.get(batchId) ?? 0) + 1;
  batchReloadGenerations.set(batchId, generation);
  try {
    const response = await ecolabelsApi.listSubmissionBatchDevices(batchId);
    if (batchReloadGenerations.get(batchId) !== generation) return false;
    batchDevices.value = {
      ...batchDevices.value,
      [batchId]: response.data.devices,
    };
    duplicateCounts.value = {
      ...duplicateCounts.value,
      ...response.data.pending_duplicate_counts,
    };
    return true;
  } catch (error) {
    // A superseded request's failure is irrelevant — its result would be discarded.
    if (batchReloadGenerations.get(batchId) === generation) {
      globalErrorHandler(error);
    }
    return false;
  }
}

async function approve(
  batchId: number,
  device: EcolabelDevice,
  overrideDuplicates = false
) {
  actingOn.value = device.id;
  const generation = expansionGeneration;
  try {
    await ecolabelsApi.approveDevice(device.id, overrideDuplicates);
    globalSuccessHandler(
      t("ecolabels.submissions.approved_toast", { name: device.name }),
      ""
    );
    await afterAction(batchId);
    if (generation === expansionGeneration) expandFirstPending(batchId);
  } catch (error: unknown) {
    if (isUnresolvedDuplicatesError(error)) {
      // Surface the pairs and an inline "approve anyway" instead of a blocking dialog;
      // the admin resolves the pairs or explicitly overrides. Deliberately NOT
      // generation-guarded: this is the failed approval's required error affordance,
      // not a convenience — swallowing it after a collapse would hide that the
      // approve the user just clicked did not happen. The panel lives in the batch's
      // expansion slot, so the batch must be (re)opened for it to actually show.
      expandedBatches.value = { ...expandedBatches.value, [batchId]: true };
      duplicatesFor.value = device.id;
      overrideRequiredFor.value = device.id;
      await loadPairs(device.id);
    } else {
      globalErrorHandler(error);
    }
  } finally {
    actingOn.value = null;
  }
}

async function approveWithOverride(batchId: number, device: EcolabelDevice) {
  overrideRequiredFor.value = null;
  duplicatesFor.value = null;
  await approve(batchId, device, true);
}

function startReject(device: EcolabelDevice) {
  rejectingDevice.value = device;
  rejectionReason.value = "";
}

async function confirmReject(batchId: number, device: EcolabelDevice) {
  actingOn.value = device.id;
  const generation = expansionGeneration;
  try {
    await ecolabelsApi.rejectDevice(device.id, rejectionReason.value.trim());
    rejectingDevice.value = null;
    await afterAction(batchId);
    if (generation === expansionGeneration) expandFirstPending(batchId);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    actingOn.value = null;
  }
}

async function toggleDuplicates(device: EcolabelDevice) {
  if (duplicatesFor.value === device.id) {
    duplicatesFor.value = null;
    return;
  }
  duplicatesFor.value = device.id;
  await loadPairs(device.id);
}

async function loadPairs(deviceId: number) {
  try {
    const response = await ecolabelsApi.getDeviceDuplicates(deviceId);
    devicePairs.value = {
      ...devicePairs.value,
      [deviceId]: response.data.pairs,
    };
  } catch (error) {
    globalErrorHandler(error);
  }
}

async function onPairResolved(batchId: number, device: EcolabelDevice) {
  await loadPairs(device.id);
  await afterAction(batchId);
}

async function afterAction(batchId: number) {
  await Promise.all([reloadBatchDevices(batchId), load({ background: true })]);
  emit("devices-changed");
}

function isUnresolvedDuplicatesError(error: unknown): boolean {
  const response = (
    error as { response?: { status?: number; data?: { error?: string } } }
  )?.response;
  return (
    response?.status === 409 &&
    response?.data?.error === "unresolved_duplicates"
  );
}

const formatDate = formatDateTimeIntoDate;

function statusBadgeClass(status: EcolabelStatus): string {
  switch (status) {
    case EcolabelStatus.PRE_APPROVED:
      return "bg-success";
    case EcolabelStatus.REJECTED:
      return "bg-danger";
    case EcolabelStatus.PENDING_REVIEW:
      return "bg-warning text-dark";
    default:
      return "bg-secondary";
  }
}
</script>

<style scoped lang="scss">
.column-selector {
  max-width: 200px;
}

.device-table-scroller {
  // Container-query units below measure this box's VISIBLE width, not its scroll width.
  container-type: inline-size;
}

// Expanded detail rows read as a distinct layer under their row.
.device-details-cell {
  padding: 0;
  background-color: var(--phoenix-body-bg);
}

// As wide as the visible area and pinned to its left edge while the table scrolls
// underneath, so the three-column field panel never depends on the table's width.
.device-details {
  position: sticky;
  left: 0;
  width: 100cqw;
}

// Internal notes: a quiet divided section under the field panel, like the upload
// grid's duplicate section.
.internal-notes {
  border-top: 1px solid var(--bs-border-color);

  .note-text {
    white-space: pre-wrap;
  }
}
</style>
